import { supabase } from '../lib/supabase';
import type {
  UserTarget,
  CareerObjective,
  CareerMilestone,
  CareerTask,
  UserNotification,
  ExecutionHealth,
  TaskStatus,
} from '../types/objectivesTasks';
import { getCurrentUserId } from '../utils/authUtils';

async function ensureUserId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (user?.id) return user.id;
  const fallback = await getCurrentUserId();
  if (fallback) return fallback;
  throw new Error('Not authenticated');
}

// ---------- Targets ----------
export async function getCurrentTarget(userId: string): Promise<UserTarget | null> {
  const { data, error } = await supabase
    .from('user_targets')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'active')
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data as UserTarget | null;
}

export async function getTargets(userId: string): Promise<UserTarget[]> {
  const { data, error } = await supabase
    .from('user_targets')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as UserTarget[];
}

export async function upsertTarget(
  userId: string,
  payload: Partial<Pick<UserTarget, 'title' | 'description' | 'start_date' | 'target_date' | 'status'>>
): Promise<UserTarget> {
  const now = new Date().toISOString();
  const existing = await getCurrentTarget(userId);
  if (existing) {
    const { data, error } = await supabase
      .from('user_targets')
      .update({ ...payload, updated_at: now })
      .eq('id', existing.id)
      .eq('user_id', userId)
      .select()
      .single();
    if (error) throw error;
    return data as UserTarget;
  }
  const { data, error } = await supabase
    .from('user_targets')
    .insert({
      user_id: userId,
      title: payload.title ?? 'My career target',
      description: payload.description ?? null,
      start_date: payload.start_date ?? null,
      target_date: payload.target_date ?? null,
      status: payload.status ?? 'active',
      updated_at: now,
    })
    .select()
    .single();
  if (error) throw error;
  return data as UserTarget;
}

// ---------- Objectives ----------
export async function getObjectives(
  userId: string,
  filters?: { targetId?: string; status?: string }
): Promise<CareerObjective[]> {
  let q = supabase
    .from('career_objectives')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });
  if (filters?.targetId) q = q.eq('target_id', filters.targetId);
  if (filters?.status) q = q.eq('status', filters.status);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as CareerObjective[];
}

export async function createObjective(
  userId: string,
  payload: Partial<Pick<CareerObjective, 'target_id' | 'title' | 'description' | 'category' | 'priority' | 'start_date' | 'end_date' | 'success_metric'>>
): Promise<CareerObjective> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('career_objectives')
    .insert({
      user_id: userId,
      target_id: payload.target_id ?? null,
      title: payload.title ?? 'New objective',
      description: payload.description ?? null,
      category: payload.category ?? 'custom',
      priority: payload.priority ?? 'medium',
      start_date: payload.start_date ?? null,
      end_date: payload.end_date ?? null,
      status: 'not_started',
      success_metric: payload.success_metric ?? null,
      updated_at: now,
    })
    .select()
    .single();
  if (error) throw error;
  return data as CareerObjective;
}

export async function updateObjective(
  userId: string,
  id: string,
  payload: Partial<Pick<CareerObjective, 'title' | 'description' | 'category' | 'priority' | 'start_date' | 'end_date' | 'status' | 'success_metric'>>
): Promise<CareerObjective> {
  const now = new Date().toISOString();
  const prev = await supabase.from('career_objectives').select('status, title').eq('id', id).eq('user_id', userId).maybeSingle();
  const prevObj = prev.data as { status?: string; title?: string } | null;
  const { data, error } = await supabase
    .from('career_objectives')
    .update({ ...payload, updated_at: now })
    .eq('id', id)
    .eq('user_id', userId)
    .select()
    .single();
  if (error) throw error;
  const next = data as CareerObjective;
  if (payload.status === 'completed' && prevObj?.status !== 'completed') {
    createNotification(userId, {
      type: 'objective_completed',
      entity_type: 'objective',
      entity_id: id,
      message: `Objective completed: ${next.title}`,
    }).catch(() => {});
  }
  return next;
}

export async function deleteObjective(userId: string, id: string): Promise<void> {
  const tasksUnderObjective = await getTasks(userId, { objectiveId: id });
  for (const task of tasksUnderObjective) {
    await deleteTask(userId, task.id);
  }
  const { error } = await supabase
    .from('career_objectives')
    .delete()
    .eq('id', id)
    .eq('user_id', userId);
  if (error) throw error;
}

// ---------- Milestones ----------
export async function getMilestonesByObjective(objectiveId: string): Promise<CareerMilestone[]> {
  const { data, error } = await supabase
    .from('career_milestones')
    .select('*')
    .eq('objective_id', objectiveId)
    .order('start_date', { ascending: true, nullsFirst: true });
  if (error) throw error;
  return (data ?? []) as CareerMilestone[];
}

export async function createMilestone(
  objectiveId: string,
  payload: Partial<Pick<CareerMilestone, 'title' | 'start_date' | 'end_date' | 'completion_percentage'>>
): Promise<CareerMilestone> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('career_milestones')
    .insert({
      objective_id: objectiveId,
      title: payload.title ?? 'New milestone',
      start_date: payload.start_date ?? null,
      end_date: payload.end_date ?? null,
      status: 'on_track',
      completion_percentage: payload.completion_percentage ?? 0,
      updated_at: now,
    })
    .select()
    .single();
  if (error) throw error;
  return data as CareerMilestone;
}

export async function updateMilestone(
  id: string,
  payload: Partial<Pick<CareerMilestone, 'title' | 'start_date' | 'end_date' | 'status' | 'completion_percentage'>>
): Promise<CareerMilestone> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('career_milestones')
    .update({ ...payload, updated_at: now })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as CareerMilestone;
}

export async function deleteMilestone(id: string): Promise<void> {
  const { error } = await supabase.from('career_milestones').delete().eq('id', id);
  if (error) throw error;
}

// ---------- Tasks ----------
export async function getTasks(
  userId: string,
  filters?: { objectiveId?: string; status?: TaskStatus }
): Promise<CareerTask[]> {
  let q = supabase
    .from('career_tasks')
    .select('*')
    .eq('user_id', userId)
    .order('due_date', { ascending: true, nullsFirst: true })
    .order('created_at', { ascending: false });
  if (filters?.objectiveId) q = q.eq('objective_id', filters.objectiveId);
  if (filters?.status) q = q.eq('status', filters.status);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as CareerTask[];
}

export async function createTask(
  userId: string,
  payload: Partial<Pick<CareerTask, 'objective_id' | 'milestone_id' | 'title' | 'description' | 'priority' | 'due_date' | 'estimated_time_minutes' | 'is_repeatable'>>
): Promise<CareerTask> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('career_tasks')
    .insert({
      user_id: userId,
      objective_id: payload.objective_id ?? null,
      milestone_id: payload.milestone_id ?? null,
      title: payload.title ?? 'New task',
      description: payload.description ?? null,
      status: 'backlog',
      priority: payload.priority ?? 'medium',
      due_date: payload.due_date ?? null,
      estimated_time_minutes: payload.estimated_time_minutes ?? null,
      is_repeatable: payload.is_repeatable ?? false,
      updated_at: now,
    })
    .select()
    .single();
  if (error) throw error;
  const task = data as CareerTask;
  await logTaskActivity(userId, task.id, 'created', null, 'backlog');
  if (payload.due_date) {
    const due = new Date(payload.due_date);
    const in3Days = new Date();
    in3Days.setDate(in3Days.getDate() + 3);
    if (due <= in3Days && due >= new Date()) {
      createNotification(userId, {
        type: 'task_due_soon',
        entity_type: 'task',
        entity_id: task.id,
        message: `Task due soon: ${task.title}`,
      }).catch(() => {});
    }
  }
  return data as CareerTask;
}

export async function updateTask(
  userId: string,
  id: string,
  payload: Partial<Pick<CareerTask, 'title' | 'description' | 'objective_id' | 'milestone_id' | 'status' | 'priority' | 'due_date' | 'estimated_time_minutes' | 'actual_time_minutes' | 'blocked_reason' | 'blocked_at'>>
): Promise<CareerTask> {
  const now = new Date().toISOString();
  const prev = await getTaskById(userId, id);
  const { data, error } = await supabase
    .from('career_tasks')
    .update({ ...payload, updated_at: now })
    .eq('id', id)
    .eq('user_id', userId)
    .select()
    .single();
  if (error) throw error;
  const next = data as CareerTask;
  if (payload.status !== undefined && payload.status !== prev?.status) {
    await logTaskActivity(userId, id, 'moved', prev?.status ?? null, payload.status);
    if (payload.status === 'blocked') {
      createNotification(userId, {
        type: 'task_blocked_long',
        entity_type: 'task',
        entity_id: id,
        message: `Task blocked: ${next.title}`,
      }).catch(() => {});
    } else if (payload.status === 'completed') {
      createNotification(userId, {
        type: 'nudge_almost_done',
        entity_type: 'task',
        entity_id: id,
        message: `Task completed: ${next.title}`,
      }).catch(() => {});
    }
  }
  return next;
}

export async function getTaskById(userId: string, id: string): Promise<CareerTask | null> {
  const { data, error } = await supabase
    .from('career_tasks')
    .select('*')
    .eq('id', id)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return data as CareerTask | null;
}

export async function deleteTask(userId: string, id: string): Promise<void> {
  const { error } = await supabase.from('career_tasks').delete().eq('id', id).eq('user_id', userId);
  if (error) throw error;
}

async function logTaskActivity(
  userId: string,
  taskId: string,
  action: 'created' | 'moved' | 'completed' | 'blocked' | 'unblocked' | 'abandoned' | 'updated',
  oldStatus: string | null,
  newStatus: string | null
): Promise<void> {
  await supabase.from('task_activity_log').insert({
    task_id: taskId,
    user_id: userId,
    action,
    old_status: oldStatus,
    new_status: newStatus,
  });
}

// ---------- Execution Health ----------
export async function getExecutionHealth(userId: string): Promise<ExecutionHealth> {
  const today = new Date().toISOString().slice(0, 10);
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekStartStr = weekStart.toISOString().slice(0, 10);

  const [objectivesRes, tasksRes] = await Promise.all([
    supabase
      .from('career_objectives')
      .select('id')
      .eq('user_id', userId)
      .in('status', ['active', 'not_started']),
    supabase
      .from('career_tasks')
      .select('id, status, due_date, updated_at')
      .eq('user_id', userId),
  ]);

  const objectives = objectivesRes.data ?? [];
  const tasks = (tasksRes.data ?? []) as Pick<CareerTask, 'id' | 'status' | 'due_date' | 'updated_at'>[];

  const activeObjectivesCount = objectives.length;
  const tasksDueToday = tasks.filter(
    (t) => t.status !== 'completed' && t.status !== 'abandoned' && t.due_date === today
  ).length;
  const overdueCount = tasks.filter(
    (t) =>
      t.status !== 'completed' &&
      t.status !== 'abandoned' &&
      t.due_date != null &&
      t.due_date < today
  ).length;
  const blockedCount = tasks.filter((t) => t.status === 'blocked').length;

  const completedThisWeek = tasks.filter(
    (t) => t.status === 'completed' && t.updated_at && t.updated_at.slice(0, 10) >= weekStartStr
  ).length;
  const activeThisWeek = tasks.filter(
    (t) => t.status !== 'abandoned' && (t.updated_at?.slice(0, 10) ?? '') >= weekStartStr
  ).length;
  const weeklyCompletionPercent =
    activeThisWeek > 0 ? Math.round((completedThisWeek / activeThisWeek) * 100) : 0;

  return {
    activeObjectivesCount,
    tasksDueToday,
    overdueCount,
    blockedCount,
    weeklyCompletionPercent,
  };
}

// ---------- Notifications ----------
export async function getNotifications(
  userId: string,
  options?: { unreadOnly?: boolean; limit?: number }
): Promise<UserNotification[]> {
  let q = supabase
    .from('user_notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (options?.unreadOnly) q = q.eq('is_read', false);
  if (options?.limit) q = q.limit(options.limit);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as UserNotification[];
}

export async function markNotificationRead(userId: string, id: string): Promise<void> {
  await supabase
    .from('user_notifications')
    .update({ is_read: true })
    .eq('id', id)
    .eq('user_id', userId);
}

export async function markAllNotificationsRead(userId: string): Promise<void> {
  await supabase.from('user_notifications').update({ is_read: true }).eq('user_id', userId);
}

/** Permanently remove every notification for this user (empty the bell tray). */
export async function clearAllUserNotifications(userId: string): Promise<void> {
  const { error } = await supabase.from('user_notifications').delete().eq('user_id', userId);
  if (error) throw error;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_REFRESH_EVENT));
  }
}

export async function createNotification(
  userId: string,
  payload: Pick<UserNotification, 'type' | 'entity_type' | 'message'> & { entity_id?: string | null }
): Promise<UserNotification> {
  const { data, error } = await supabase
    .from('user_notifications')
    .insert({
      user_id: userId,
      type: payload.type,
      entity_type: payload.entity_type,
      entity_id: payload.entity_id ?? null,
      message: payload.message,
      is_read: false,
    })
    .select()
    .single();
  if (error) throw error;
  return data as UserNotification;
}

/** Dispatched after a new row is inserted so open trays refresh their badge/list. */
export const NOTIFICATIONS_REFRESH_EVENT = 'ggj:notifications-refresh';

/**
 * Persist a bell-tray notification and ping any mounted NotificationTrayButton
 * to refresh unread count / list. Failures are logged and never thrown.
 */
export async function pushUserNotification(
  userId: string,
  payload: Pick<UserNotification, 'type' | 'entity_type' | 'message'> & { entity_id?: string | null },
): Promise<UserNotification | null> {
  try {
    const row = await createNotification(userId, payload);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(NOTIFICATIONS_REFRESH_EVENT));
    }
    return row;
  } catch (err) {
    console.warn('[pushUserNotification] failed:', err);
    return null;
  }
}

/** Sync task due-soon and overdue notifications when user opens Objectives page. Deduplicates by existing unread notifications. */
export async function syncTaskReminderNotifications(userId: string): Promise<void> {
  const today = new Date().toISOString().slice(0, 10);
  const in2Days = new Date();
  in2Days.setDate(in2Days.getDate() + 2);
  const cutoff = in2Days.toISOString().slice(0, 10);

  const { data: tasks } = await supabase
    .from('career_tasks')
    .select('id, title, due_date, status')
    .eq('user_id', userId)
    .not('due_date', 'is', null)
    .lte('due_date', cutoff);
  const taskList = ((tasks ?? []) as { id: string; title: string; due_date: string; status: string }[]).filter(
    (t) => t.status !== 'completed' && t.status !== 'abandoned'
  );
  if (taskList.length === 0) return;

  const { data: existing } = await supabase
    .from('user_notifications')
    .select('entity_id')
    .eq('user_id', userId)
    .eq('entity_type', 'task')
    .eq('is_read', false)
    .in('type', ['task_due_soon', 'task_overdue']);
  const notifiedTaskIds = new Set((existing ?? []).map((r) => r.entity_id).filter(Boolean));

  for (const t of taskList) {
    if (notifiedTaskIds.has(t.id)) continue;
    const isOverdue = t.due_date < today;
    createNotification(userId, {
      type: isOverdue ? 'task_overdue' : 'task_due_soon',
      entity_type: 'task',
      entity_id: t.id,
      message: isOverdue ? `Task overdue: ${t.title}` : `Task due soon: ${t.title}`,
    }).catch(() => {});
    notifiedTaskIds.add(t.id);
  }
}
